import React from 'react';
import PropTypes from 'prop-types';
import { Card, Row, Col, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faCheckCircle,
    faFilePdf,
    faEnvelope,
    faPhone,
    faGlobe,
    faBuilding,
    faIdCard
} from '@fortawesome/free-solid-svg-icons';

/**
 * Ficha de Identificación del Distribuidor.
 * Muestra información 100% real proveniente de la base de datos del cliente.
 */
const DistribuidorFichaCard = ({
    distribuidor = {},
    onDescargarPdf,
    descargandoPdf = false
}) => {
    const nombreCompleto =
        distribuidor.vNombreDistribuidor ||
        `${distribuidor.vNombre || ''} ${distribuidor.vApellidos || ''}`.trim() ||
        'Distribuidor';

    const idCliente = distribuidor.idClienteAkasia || 'N/A';
    const totalCompras =
        distribuidor.dTotalVenta !== undefined
            ? parseFloat(distribuidor.dTotalVenta)
            : parseFloat(distribuidor.dMontoVendido || 0);

    const telefono = distribuidor.vTelefonoPersonal || distribuidor.vTelefonoTrabajo || '';

    return (
        <Card
            className="shadow-sm mb-3 position-relative overflow-hidden"
            style={{
                borderRadius: '12px',
                borderTop: '4px solid #0284c7'
            }}
        >
            <Card.Body className="p-3 p-md-4">
                {/* Encabezado Principal: Identidad, Badges y Botón de Descarga */}
                <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 pb-3 border-bottom border-200">
                    <div className="d-flex align-items-center gap-3">
                        {/* Avatar / Logo Akasia con check de verificación */}
                        <div className="position-relative">
                            <div
                                className="d-flex align-items-center justify-content-center text-white fw-bold shadow-sm"
                                style={{
                                    width: '56px',
                                    height: '56px',
                                    borderRadius: '14px',
                                    background: 'linear-gradient(135deg, #0284c7, #2563eb)',
                                    fontSize: '1.25rem'
                                }}
                            >
                                <FontAwesomeIcon icon={faBuilding} />
                            </div>
                            <span
                                className="position-absolute bottom-0 end-0 rounded-circle d-flex align-items-center justify-content-center"
                                style={{
                                    width: '20px',
                                    height: '20px',
                                    transform: 'translate(25%, 25%)'
                                }}
                            >
                                <FontAwesomeIcon icon={faCheckCircle} className="text-success rounded-circle" style={{ fontSize: '1.05rem', backgroundColor: 'var(--falcon-card-bg, #fff)' }} />
                            </span>
                        </div>

                        {/* Nombre y Badges Reales */}
                        <div>
                            <div className="d-flex flex-wrap align-items-center gap-2 mb-1">
                                <h4
                                    className="mb-0 fw-bold text-capitalize text-1000"
                                    style={{ fontSize: '1.35rem' }}
                                >
                                    {nombreCompleto}
                                </h4>
                                {distribuidor.vDescripcionTipo && (
                                    <span
                                        className="badge bg-soft-primary text-primary px-2 py-1 rounded-pill fw-semibold"
                                        style={{ fontSize: '0.75rem' }}
                                    >
                                        {distribuidor.vDescripcionTipo}
                                    </span>
                                )}
                                {distribuidor.bIsActive !== undefined && (
                                    <span
                                        className={`badge ${distribuidor.bIsActive ? 'bg-soft-success text-success' : 'bg-soft-danger text-danger'} px-2 py-1 rounded-pill fw-semibold`}
                                        style={{ fontSize: '0.75rem' }}
                                    >
                                        ● {distribuidor.bIsActive ? 'ACTIVO' : 'INACTIVO'}
                                    </span>
                                )}
                            </div>

                            <div className="d-flex flex-wrap align-items-center gap-2" style={{ fontSize: '0.85rem' }}>
                                <span className="px-2 py-0.5 rounded border border-200 bg-100 text-600 fw-semibold">
                                    ID Distribuidor: #{idCliente}
                                </span>
                                {distribuidor.vPais && (
                                    <span className="text-600">
                                        <FontAwesomeIcon icon={faGlobe} className="me-1 text-primary" />
                                        {distribuidor.vPais}
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Botón Descargar Ficha PDF */}
                    <div>
                        <Button
                            variant="falcon-default"
                            size="sm"
                            className="d-flex align-items-center gap-2 px-3 py-2 text-decoration-none shadow-sm"
                            onClick={onDescargarPdf}
                            disabled={descargandoPdf}
                            style={{
                                fontWeight: 600,
                                fontSize: '0.85rem'
                            }}
                        >
                            <FontAwesomeIcon icon={faFilePdf} className="text-danger" />
                            <span>{descargandoPdf ? 'Generando PDF...' : 'Descargar Ficha PDF'}</span>
                        </Button>
                    </div>
                </div>

                {/* Barra de Indicadores Clave del Distribuidor */}
                <div className="py-3 border-bottom border-200">
                    <Row className="g-3 text-center text-md-start">
                        <Col xs={12} sm={4}>
                            <div className="text-uppercase fw-bold text-600" style={{ fontSize: '0.70rem', letterSpacing: '0.5px' }}>
                                Total Ventas
                            </div>
                            <div className="fw-bold text-warning" style={{ fontSize: '1.15rem' }}>
                                ${totalCompras.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}{' '}
                                <span className="text-600" style={{ fontSize: '0.8rem' }}>MXN</span>
                            </div>
                        </Col>

                        <Col xs={12} sm={4}>
                            <div className="text-uppercase fw-bold text-600" style={{ fontSize: '0.70rem', letterSpacing: '0.5px' }}>
                                Total Órdenes
                            </div>
                            <div className="fw-bold text-1000" style={{ fontSize: '1.15rem' }}>
                                {distribuidor.iTotalCompras !== undefined ? distribuidor.iTotalCompras : (distribuidor.totalCompras || 0)}
                            </div>
                        </Col>

                        <Col xs={12} sm={4}>
                            <div className="text-uppercase fw-bold text-600" style={{ fontSize: '0.70rem', letterSpacing: '0.5px' }}>
                                Ubicación / Estado
                            </div>
                            <div className="fw-bold text-truncate text-1000" style={{ fontSize: '1.05rem' }}>
                                {distribuidor.vEstadoDepProv || distribuidor.vPais || 'N/A'}
                            </div>
                        </Col>
                    </Row>
                </div>

                {/* Dos Columnas: Contacto General e Información Fiscal */}
                <Row className="g-3 mt-1">
                    {/* Panel 1: Contacto General */}
                    <Col xs={12} lg={6}>
                        <div className="p-3 rounded-3 h-100 bg-100 border border-200">
                            <div className="d-flex align-items-center justify-content-between mb-3 pb-2 border-bottom border-200">
                                <h6 className="mb-0 fw-bold text-1000">
                                    Contacto General
                                </h6>
                            </div>

                            <div className="d-flex flex-column gap-2" style={{ fontSize: '0.82rem' }}>
                                <div className="d-flex justify-content-between py-1 border-bottom border-200">
                                    <span className="text-600">Teléfono / WhatsApp</span>
                                    {telefono ? (
                                        <a
                                            href={`https://wa.me/${telefono}`}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="fw-bold text-decoration-none text-1000"
                                        >
                                            <FontAwesomeIcon icon={faPhone} className="me-1 text-success" />
                                            {telefono}
                                        </a>
                                    ) : (
                                        <span className="fw-bold text-600">N/A</span>
                                    )}
                                </div>

                                <div className="d-flex justify-content-between py-1 border-bottom border-200">
                                    <span className="text-600">Correo Electrónico</span>
                                    {distribuidor.vEmail ? (
                                        <a
                                            href={`mailto:${distribuidor.vEmail}`}
                                            className="fw-bold text-decoration-none text-truncate text-1000"
                                            style={{ maxWidth: '240px' }}
                                        >
                                            <FontAwesomeIcon icon={faEnvelope} className="me-1 text-primary" />
                                            {distribuidor.vEmail.toUpperCase()}
                                        </a>
                                    ) : (
                                        <span className="fw-bold text-600">N/A</span>
                                    )}
                                </div>

                                <div className="d-flex justify-content-between py-1 border-bottom border-200">
                                    <span className="text-600">País / Estado</span>
                                    <span className="fw-bold text-1000">
                                        {distribuidor.vPais || 'N/A'}{distribuidor.vEstadoDepProv ? ` / ${distribuidor.vEstadoDepProv}` : ''}
                                    </span>
                                </div>

                                <div className="d-flex justify-content-between py-1 border-bottom border-200">
                                    <span className="text-600">Fecha de Registro</span>
                                    <span className="fw-bold text-1000">
                                        {distribuidor.reg_desde || distribuidor.joinDate || 'N/A'}
                                    </span>
                                </div>

                                <div className="d-flex justify-content-between py-1">
                                    <span className="text-600">Agente Asignado</span>
                                    <span className="fw-bold text-primary text-uppercase">
                                        {distribuidor.vAgente || distribuidor.vCreatedUser || 'N/A'}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </Col>

                    {/* Panel 2: Información Fiscal */}
                    <Col xs={12} lg={6}>
                        <div className="p-3 rounded-3 h-100 bg-100 border border-200">
                            <div className="d-flex align-items-center justify-content-between mb-3 pb-2 border-bottom border-200">
                                <h6 className="mb-0 fw-bold text-1000">
                                    Información Fiscal
                                </h6>
                            </div>

                            <div className="d-flex flex-column gap-2" style={{ fontSize: '0.82rem' }}>
                                <div className="d-flex justify-content-between py-1 border-bottom border-200">
                                    <span className="text-600">Razón Social</span>
                                    <span className="fw-bold text-truncate text-1000" style={{ maxWidth: '240px' }}>
                                        {distribuidor.vRazonSocial || 'N/A'}
                                    </span>
                                </div>

                                <div className="d-flex justify-content-between py-1 border-bottom border-200">
                                    <span className="text-600">RFC / Identificación</span>
                                    <span className="fw-bold text-1000">
                                        <FontAwesomeIcon icon={faIdCard} className="me-1 text-info" />
                                        {distribuidor.vRfc || 'N/A'}
                                    </span>
                                </div>

                                <div className="d-flex justify-content-between py-1 border-bottom border-200">
                                    <span className="text-600">Núm. Reg. Trib.</span>
                                    <span className="fw-bold text-1000">
                                        {distribuidor.vNumRegTrib || 'N/A'}
                                    </span>
                                </div>

                                <div className="d-flex justify-content-between py-1 border-bottom border-200">
                                    <span className="text-600">País Facturación</span>
                                    <span className="fw-bold text-1000">
                                        {distribuidor.vPaisFacturacion || distribuidor.vPais || 'N/A'}
                                    </span>
                                </div>

                                <div className="d-flex justify-content-between py-1 border-bottom border-200">
                                    <span className="text-600">Régimen Fiscal</span>
                                    <span className="fw-bold text-truncate text-1000" style={{ maxWidth: '240px' }}>
                                        {distribuidor.vDescripcionRegimenFiscal || distribuidor.vCodigoRegimen || 'N/A'}
                                    </span>
                                </div>

                                <div className="d-flex justify-content-between py-1">
                                    <span className="text-600">Uso CFDI</span>
                                    <span className="fw-bold text-truncate text-1000" style={{ maxWidth: '240px' }}>
                                        {distribuidor.vDescripcionUsoCFDI || distribuidor.vCodigoUso || 'N/A'}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </Col>
                </Row>
            </Card.Body>
        </Card>
    );
};

DistribuidorFichaCard.propTypes = {
    distribuidor: PropTypes.object.isRequired,
    onDescargarPdf: PropTypes.func.isRequired,
    descargandoPdf: PropTypes.bool
};

export default DistribuidorFichaCard;
